import { EveRoutes, EveAuthorizationService } from '@ver-uds/e-verify';
import React from 'react';
import { EverifyUserRole, MyI9TrainingStatus, User } from '@vis-auth/vis-user-client';
import { Helmet } from 'react-helmet-async';
import { useAccount } from '../../context/Account/AccountContext';
import { useUser } from '../../context/User/UserContext';
import I9Constants from '../I9Constants/I9Constants';

const pageTitle = 'E-Verify Site Map';

interface SiteMapLinkProps {
  route: string;
  label: string;
}

interface SiteMapSectionProps {
  title: string;
  children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const SiteMapLink = ({ route, label }: SiteMapLinkProps) => {
  return (
    <div className="margin-bottom-2">
      <a className="usa-link" href={route}>
        {label}
      </a>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const SiteMapSection = ({ title, children }: SiteMapSectionProps) => {
  return (
    <div className="desktop:grid-col-4 tablet:grid-col-6 margin-top-neg-2 margin-bottom-7">
      <h3>{title}</h3>
      {children}
    </div>
  );
};

const SiteMap = (): JSX.Element => {
  const { roles, myI9TrainingStatus } = useUser() as User;
  const { type, webServiceAccess, myI9 } = useAccount();

  const myI9TrainingComplete =
    myI9TrainingStatus === MyI9TrainingStatus.COMPLETE || myI9TrainingStatus === MyI9TrainingStatus.OVERRIDDEN;
  const myI9Enabled = myI9?.enabled ?? false;
  const showTraining: boolean = myI9TrainingComplete && myI9Enabled;

  const eveAuthorizationService = new EveAuthorizationService({
    userRoles: roles as EverifyUserRole[],
    accountType: type,
    webServiceAccess: webServiceAccess.enabled,
  });
  return (
    <>
      <Helmet>
        <title>Site Map</title>
      </Helmet>
      <div className="grid-container margin-top-4">
        <h1 data-testid="sitemap">{pageTitle}</h1>
        <div className="grid-row margin-top-4">
          {eveAuthorizationService.authorizeCasesPaths() && (
            <SiteMapSection title="Cases">
              <SiteMapLink route={EveRoutes.CASES_NEW} label="Start New Case" />
              <SiteMapLink route={EveRoutes.CASES_VIEW} label="View Cases" />
            </SiteMapSection>
          )}
          <SiteMapSection title="Reports">
            <SiteMapLink route={EveRoutes.REPORTS} label="Reports" />
          </SiteMapSection>
          {eveAuthorizationService.authorizeWebServices() && (
            <SiteMapSection title="Web Services">
              <SiteMapLink route={EveRoutes.WS_SELF_TEST_SIMULATOR} label="Self Test Emulator" />
              <SiteMapLink route={EveRoutes.COMPANY_USERS_NEW} label="Add New WS User" />
              <SiteMapLink route={EveRoutes.COMPANY_USERS} label="View WS Users" />
            </SiteMapSection>
          )}
          {eveAuthorizationService.authorizeClientPaths() && (
            <SiteMapSection title="Clients">
              <SiteMapLink route={EveRoutes.COMPANY_CLIENTS_NEW} label="Add New Client" />
              <SiteMapLink route={EveRoutes.COMPANY_CLIENTS_VIEW} label="View Clients" />
            </SiteMapSection>
          )}
          {eveAuthorizationService.authorizeCorporatePaths() && (
            <SiteMapSection title="Company Locations">
              <SiteMapLink route={EveRoutes.CORPORATE_COMPANY_LOCATIONS_NEW} label="Add New Company Location" />
              <SiteMapLink route={EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW} label="View Company Locations" />
              <SiteMapLink
                route={EveRoutes.CORPORATE_COMPANY_LOCATIONS_USERS_NEW}
                label="Add New Company Location User"
              />
              <SiteMapLink route={EveRoutes.CORPORATE_COMPANY_LOCATIONS_USERS} label="View Company Location Users" />
            </SiteMapSection>
          )}
          <SiteMapSection title="Resources">
            <SiteMapLink route={EveRoutes.RESOURCES_VIEW} label="View Essential Resources" />
            <SiteMapLink route={EveRoutes.TUTORIAL} label="Take Tutorial" />
            {showTraining && (
              <SiteMapLink
                route={EveRoutes.RESOURCES_VIEW_TRAINING}
                label={`View ${I9Constants.PROGRAM_NAME} Training`}
              />
            )}
            <SiteMapLink route={EveRoutes.RESOURCES_MANUAL} label="View User Manual" />
            <SiteMapLink route={EveRoutes.RESOURCES_NEWS} label="E-Verify News and Announcements" />
            <SiteMapLink route={EveRoutes.RESOURCES_CONTACT_US} label="Contact Us" />
          </SiteMapSection>
          {eveAuthorizationService.authorizeCompanyPaths() && (
            <SiteMapSection title="Company Account">
              <SiteMapLink route={EveRoutes.COMPANY_PROFILE_VIEW} label="Company Profile" />
              <SiteMapLink route={EveRoutes.COMPANY_HIRING_SITES_EDIT} label="Hiring Sites" />
              <SiteMapLink route={EveRoutes.COMPANY_USERS_NEW} label="Add New User" />
              <SiteMapLink route={EveRoutes.COMPANY_USERS} label="View Existing Users" />
              <SiteMapLink route={EveRoutes.COMPANY_CLOSE} label="Close Company Account" />
            </SiteMapSection>
          )}
          {eveAuthorizationService.authorizeCorporatePaths() && (
            <SiteMapSection title="Corporate Account">
              <SiteMapLink route={EveRoutes.CORPORATE_PROFILE_VIEW} label="Corporate Profile" />
              <SiteMapLink route={EveRoutes.CORPORATE_USERS_NEW} label="Add New Administrator" />
              <SiteMapLink route={EveRoutes.CORPORATE_USERS} label="View Existing Administrators" />
              <SiteMapLink route={EveRoutes.CORPORATE_CLOSE} label="Close Corporate Account" />
            </SiteMapSection>
          )}
          <SiteMapSection title="User Account">
            <SiteMapLink route={EveRoutes.USER_PROFILE} label="User Profile" />
            <SiteMapLink route={EveRoutes.USER_PASSWORD_EDIT} label="Change Password" />
          </SiteMapSection>
        </div>
      </div>
    </>
  );
};

export default SiteMap;
